import { useEffect, useRef } from 'react';

import { getGtmEventData, updateProfile } from 'actions/user';
import { GTM_EVENTS, TRACKING_INFO_KEYS_FOR_EVENT_TRACKING } from 'constants/gtmTracking';
import { FirstTradeUtils, FourteenDayCommissionUtils } from 'helpers/gtmTracking';
import { dispatchGTMEvent } from 'helpers/utils';
import {
  inrConversionRateSelector,
  selectUserRegistrationDate,
  selectUserTrackingInfo,
  getUserId,
} from 'selectors/otherSelectors';
import {
  // selectUserLeastFirstTradeDate,
  // selectHasUserDoneFirstTrade,
  selectUserEmailSha256,
  selectUserFirstTradeTime,
} from 'selectors/user';
import { useAppDispatch, useAppSelector } from 'storeHooks';
import { FirstTradeGtmResult, FourteenDayCommissionResult } from 'types/actions/user';

// not urgent
const DELAY_TIME = 5000;

const useTrackTradeData = () => {
  const trackingInfo = useAppSelector(selectUserTrackingInfo);

  const appDispatch = useAppDispatch();

  const registrationDate = useAppSelector(selectUserRegistrationDate);

  const sha256EmailHexString = useAppSelector(selectUserEmailSha256);

  // const hasUserDoneFirstTrade = useAppSelector(selectHasUserDoneFirstTrade);

  const firstTradeTime = useAppSelector(selectUserFirstTradeTime);

  const shouldTrackCriteoEvents = !trackingInfo?.appsflyer_id;

  const inrConversionRate = useAppSelector(inrConversionRateSelector);

  const userId = useAppSelector(getUserId);

  const firstTradeTimerIdRef = useRef<NodeJS.Timeout>();

  const fourteenDaysCommissionTimerIdRef = useRef<NodeJS.Timeout>();

  const firstTradeRequestStarted = useRef<boolean>(false);
  const fourteenDayCommissionRequestStarted = useRef<boolean>(false);

  useEffect(() => {
    if (document.hidden) return;

    // setTimeout(() => {
    //   dispatchGTMEvent(GTM_EVENTS.FIRST_TRADE, {
    //     ...FirstTradeUtils.eventValuesFormatter({
    //       commission: '200',
    //       shouldTrackCriteoEvents: true,
    //       inrConversionRate: 1,
    //     }),
    //     userEmailSha256: sha256EmailHexString,
    //     userId,
    //   });
    // }, 3000);

    firstTradeTimerIdRef.current = setTimeout(() => {
      if (
        FirstTradeUtils.shouldTrack(
          trackingInfo?.is_first_trade_gtm_tracked,
          registrationDate || '',
          Boolean(firstTradeTime)
        ) &&
        !firstTradeRequestStarted.current
      ) {
        firstTradeRequestStarted.current = true;

        appDispatch(getGtmEventData({ event_name: GTM_EVENTS.FIRST_TRADE })).then(
          response => {
            const { result }: { result: FirstTradeGtmResult } = response;

            appDispatch(
              updateProfile({
                [TRACKING_INFO_KEYS_FOR_EVENT_TRACKING[GTM_EVENTS.FIRST_TRADE]]: true,
              })
            ).then(() => {
              dispatchGTMEvent(GTM_EVENTS.FIRST_TRADE, {
                ...FirstTradeUtils.eventValuesFormatter({
                  commission: result.event_value.commission,
                  shouldTrackCriteoEvents,
                  inrConversionRate,
                }),
                userEmailSha256: sha256EmailHexString,
                userId,
              });
            });
          }
        );
      }

      return () => {
        if (firstTradeTimerIdRef.current) {
          clearTimeout(firstTradeTimerIdRef.current);
        }
      };
    }, DELAY_TIME);
  }, [
    appDispatch,
    registrationDate,
    shouldTrackCriteoEvents,
    trackingInfo?.is_first_trade_gtm_tracked,
    firstTradeTime,
    inrConversionRate,
    sha256EmailHexString,
    userId,
  ]);

  useEffect(() => {
    // tab is in background ,
    if (document.hidden || fourteenDayCommissionRequestStarted.current) return;

    fourteenDaysCommissionTimerIdRef.current = setTimeout(() => {
      if (
        FourteenDayCommissionUtils.shouldTrack(
          trackingInfo?.is_first_trade_gtm_tracked,
          trackingInfo?.is_14D_fees_gtm_tracked,
          firstTradeTime
        ) &&
        !fourteenDayCommissionRequestStarted.current
      ) {
        fourteenDayCommissionRequestStarted.current = true;

        appDispatch(
          getGtmEventData({ event_name: GTM_EVENTS.FOURTEEN_DAY_COMMISSION })
        ).then(response => {
          const { result }: { result: FourteenDayCommissionResult } = response;

          appDispatch(
            updateProfile({
              [TRACKING_INFO_KEYS_FOR_EVENT_TRACKING[GTM_EVENTS.FOURTEEN_DAY_COMMISSION]]:
                true,
            })
          ).then(() => {
            dispatchGTMEvent(GTM_EVENTS.FOURTEEN_DAY_COMMISSION, {
              ...FourteenDayCommissionUtils.eventValuesFormatter({
                commission: result.event_value.commission,
                shouldTrackCriteoEvents,
                inrConversionRate,
              }),
              userEmailSha256: sha256EmailHexString,
              userId,
            });
          });
        });
      }

      return () => {
        if (fourteenDaysCommissionTimerIdRef.current) {
          clearTimeout(fourteenDaysCommissionTimerIdRef.current);
        }
      };
    }, DELAY_TIME);
  }, [
    appDispatch,
    firstTradeTime,
    shouldTrackCriteoEvents,
    trackingInfo?.is_14D_fees_gtm_tracked,
    trackingInfo?.is_first_trade_gtm_tracked,
    inrConversionRate,
    sha256EmailHexString,
    userId,
  ]);

  return null;
};

export { useTrackTradeData };
